import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {EntityKey} from '../entity/entity';
import {EntityLineHeader} from '../entity/entity-line-header';
import {EntitySaveResponse} from '../entity/entity-save-response';
import {Riser} from '../products/riser';
import {GoogleService} from './google-service';
import {EntityLine} from '../entity/entity-line';

@Service()
export class EntityLineService {
  private readonly http = inject(HttpClient);
  private readonly googleService = inject(GoogleService);


  private urlBase = environment.serviceUrl;

  public getEntityLines(key: EntityKey): Observable<EntityLineHeader[]> {
    let url = this.urlBase + 'entityLines';
    let body = JSON.stringify(key);
    return this.http.post<EntityLineHeader[]>(url, body, {headers: this.getPostHeaders()});
  }

  public getRiserLines(key: EntityKey): Observable<Riser[]> {
    let url = this.urlBase + 'entityLines/riserLines/' + key.entityName + '/' + key.entityId + '/' + key.entityRevision;
    return this.http.get<Riser[]>(url, {headers: this.getRequestHeaders()});
  }

  public deleteLines(lines: EntityLineHeader[]): Observable<any> {
    let url = this.urlBase + 'entityLines/delete';
    let body = JSON.stringify(lines);
    return this.http.post(url, body, {headers: this.getPostHeaders(), responseType: 'text'});
  }

  private getPostHeaders(): HttpHeaders {
    return this.getRequestHeaders().set("Content-Type", "application/json");
  }

  private getRequestHeaders(): HttpHeaders {
    return new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
  }

  public updateLeadTimes(entityKey: EntityKey, leadTime: string): Observable<EntityLineHeader[]> {
    let url = this.urlBase + 'entityLines/updateLeadTime/' + leadTime;
    let body = JSON.stringify(entityKey);
    return this.http.post<EntityLineHeader[]>(url, body, {headers: this.getPostHeaders()});
  }

  public updateQuantity(line: EntityLineHeader): Observable<EntityLineHeader> {
    let url = this.urlBase + 'entityLines/updateQuantity';
    let body = JSON.stringify(line);
    return this.http.post<EntityLineHeader>(url, body, {headers: this.getPostHeaders()});
  }

  public updateLine(line: EntityLineHeader): Observable<EntityLineHeader> {
    let url = this.urlBase + 'entityLines/updateLine';
    let body = JSON.stringify(line);
    return this.http.post<EntityLineHeader>(url, body, { headers: this.getPostHeaders() });
  }

  getFullEntityLine(header: EntityLineHeader): Observable<EntityLine> {
    return this.getEntireEntityLine(header.key.entityName, header.key.entityId, header.key.entityRevision, header.lineId);
  }

  getEntireEntityLine(entityName: string, entityId: string, revision: number, lineId: string): Observable<EntityLine> {
    const url = this.urlBase + 'entityLines/' + entityName + '/' + entityId + '/' + revision + '/' + lineId;
    return this.http.get<EntityLine>(url, {headers: this.getRequestHeaders()});
  }

  saveFullEntityLine(entityLine: EntityLine): Observable<EntityLine> {
    const url = this.urlBase + 'entityLines/save';
    return this.http.post<EntityLine>(url, JSON.stringify(entityLine), {headers: this.getPostHeaders()});
  }

  public importLines(entityId: string, entityRevision: number, fileBytes: Uint8Array): Observable<EntitySaveResponse> {
    let url = this.urlBase + 'entityLines/importLines/' + entityId + '/' + entityRevision;
    let headers = this.getRequestHeaders().set('Content-Type', 'application/octet-stream');
    return this.http.post<EntitySaveResponse>(url, fileBytes, {headers: headers});
  }

  public getImportTemplate(): Observable<Blob> {
    const url = this.urlBase + 'entityLines/importTemplate';
    return this.http.get(url, {headers: this.getRequestHeaders(), responseType: 'blob'});
  }

}
